import { useEffect, useCallback, useRef, useState } from 'react';
import { useWebSocket } from '@/hooks/use-websocket';
import { logger } from '@/utils';

export interface SchemaDiscoveryEvents {
  'schema-discovery-started': {
    dataSourceId: string;
    estimatedTime: number;
    sessionId: string;
  };
  'schema-discovery-progress': {
    sessionId: string;
    progress: number;
    stage: 'connecting' | 'analyzing' | 'enhancing' | 'validating' | 'complete';
    message: string;
    details?: string;
  };
  'schema-discovery-completed': {
    sessionId: string;
    schema: any;
    confidence: number;
    processingTime: number;
  };
  'schema-validation-result': {
    sessionId: string;
    isValid: boolean;
    errors: Array<{
      field: string;
      message: string;
      severity: 'error' | 'warning';
    }>;
    warnings?: Array<{
      field: string;
      message: string;
    }>;
  };
  'cube-schema-generated': {
    sessionId: string;
    cubeSchema: any;
    optimizations: string[];
    deploymentReady: boolean;
  };
  'schema-discovery-error': {
    sessionId: string;
    error: string;
    details?: any;
    recoverable: boolean;
  };
}

export interface SchemaDiscoveryState {
  isConnected: boolean;
  currentSession: string | null;
  progress: number;
  stage: 'idle' | 'connecting' | 'analyzing' | 'enhancing' | 'validating' | 'complete' | 'error';
  message: string;
  details?: string;
  discoveredSchema: any | null;
  cubeSchema: any | null;
  errors: Array<{
    field: string;
    message: string;
    severity: 'error' | 'warning';
  }>;
  confidence: number;
}

interface UseSchemaDiscoveryWebSocketOptions {
  autoConnect?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
}

type EventHandlers = {
  [K in keyof SchemaDiscoveryEvents]?: (data: SchemaDiscoveryEvents[K]) => void;
};

export const useSchemaDiscoveryWebSocket = (
  options: UseSchemaDiscoveryWebSocketOptions = {}
) => {
  const {
    autoConnect = true,
    retryAttempts = 3,
    retryDelay = 1000
  } = options;

  const [state, setState] = useState<SchemaDiscoveryState>({
    isConnected: false,
    currentSession: null,
    progress: 0,
    stage: 'idle',
    message: '',
    discoveredSchema: null,
    cubeSchema: null,
    errors: [],
    confidence: 0
  });

  const eventHandlersRef = useRef<EventHandlers>({});
  const sessionTimeoutRef = useRef<NodeJS.Timeout>();

  const { socket, isConnected, connect, disconnect, emit } = useWebSocket({
    namespace: '/analytics',
    autoConnect,
    retryAttempts,
    retryDelay
  });

  // Update connection state
  useEffect(() => {
    setState(prev => ({ ...prev, isConnected }));
  }, [isConnected]);

  // Set up event listeners
  useEffect(() => {
    if (!socket) return;

    const handleSchemaDiscoveryStarted = (data: SchemaDiscoveryEvents['schema-discovery-started']) => {
      logger.info('Schema discovery started', data);
      setState(prev => ({
        ...prev,
        currentSession: data.sessionId,
        stage: 'connecting',
        progress: 0,
        message: 'Discovery session started',
        errors: []
      }));

      // Set up session timeout
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      sessionTimeoutRef.current = setTimeout(() => {
        handleSessionTimeout(data.sessionId);
      }, data.estimatedTime * 2000); // 2x estimated time as timeout

      eventHandlersRef.current['schema-discovery-started']?.(data);
    };

    const handleSchemaDiscoveryProgress = (data: SchemaDiscoveryEvents['schema-discovery-progress']) => {
      logger.info('Schema discovery progress', data);
      setState(prev => ({
        ...prev,
        progress: data.progress,
        stage: data.stage,
        message: data.message,
        details: data.details
      }));

      eventHandlersRef.current['schema-discovery-progress']?.(data);
    };

    const handleSchemaDiscoveryCompleted = (data: SchemaDiscoveryEvents['schema-discovery-completed']) => {
      logger.info('Schema discovery completed', data);
      setState(prev => ({
        ...prev,
        stage: 'complete',
        progress: 100,
        message: 'Schema discovery completed successfully',
        discoveredSchema: data.schema,
        confidence: data.confidence
      }));

      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }

      eventHandlersRef.current['schema-discovery-completed']?.(data);
    };

    const handleSchemaValidationResult = (data: SchemaDiscoveryEvents['schema-validation-result']) => {
      logger.info('Schema validation result', data);
      setState(prev => ({
        ...prev,
        errors: data.errors
      }));

      eventHandlersRef.current['schema-validation-result']?.(data);
    };

    const handleCubeSchemaGenerated = (data: SchemaDiscoveryEvents['cube-schema-generated']) => {
      logger.info('Cube schema generated', data);
      setState(prev => ({
        ...prev,
        cubeSchema: data.cubeSchema,
        message: 'Cube.js schema generated successfully'
      }));

      eventHandlersRef.current['cube-schema-generated']?.(data);
    };

    const handleSchemaDiscoveryError = (data: SchemaDiscoveryEvents['schema-discovery-error']) => {
      logger.error('Schema discovery error', data);
      setState(prev => ({
        ...prev,
        stage: 'error',
        message: data.error,
        details: data.details ? JSON.stringify(data.details) : undefined
      }));

      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }

      eventHandlersRef.current['schema-discovery-error']?.(data);
    };

    // Register event listeners
    socket.on('schema-discovery-started', handleSchemaDiscoveryStarted);
    socket.on('schema-discovery-progress', handleSchemaDiscoveryProgress);
    socket.on('schema-discovery-completed', handleSchemaDiscoveryCompleted);
    socket.on('schema-validation-result', handleSchemaValidationResult);
    socket.on('cube-schema-generated', handleCubeSchemaGenerated);
    socket.on('schema-discovery-error', handleSchemaDiscoveryError);

    return () => {
      socket.off('schema-discovery-started', handleSchemaDiscoveryStarted);
      socket.off('schema-discovery-progress', handleSchemaDiscoveryProgress);
      socket.off('schema-discovery-completed', handleSchemaDiscoveryCompleted);
      socket.off('schema-validation-result', handleSchemaValidationResult);
      socket.off('cube-schema-generated', handleCubeSchemaGenerated);
      socket.off('schema-discovery-error', handleSchemaDiscoveryError);
    };
  }, [socket]);

  const handleSessionTimeout = useCallback((sessionId: string) => {
    logger.warn('Schema discovery session timeout', { sessionId });
    setState(prev => ({
      ...prev,
      stage: 'error',
      message: 'Session timeout - discovery took longer than expected',
      details: 'The schema discovery process exceeded the expected time limit'
    }));
  }, []);

  // Event handler registration
  const on = useCallback(<K extends keyof SchemaDiscoveryEvents>(
    event: K,
    handler: (data: SchemaDiscoveryEvents[K]) => void
  ) => {
    eventHandlersRef.current[event] = handler;
  }, []);

  const off = useCallback(<K extends keyof SchemaDiscoveryEvents>(event: K) => {
    delete eventHandlersRef.current[event];
  }, []);

  // Schema discovery actions
  const startSchemaDiscovery = useCallback((dataSourceId: string, options?: {
    industryContext?: string;
    aiEnhancement?: boolean;
    validationLevel?: 'basic' | 'comprehensive';
  }) => {
    if (!socket || !isConnected) {
      logger.error('Cannot start schema discovery: WebSocket not connected');
      return;
    }

    const discoveryOptions = {
      dataSourceId,
      industryContext: options?.industryContext || 'general',
      aiEnhancement: options?.aiEnhancement !== false,
      validationLevel: options?.validationLevel || 'comprehensive',
      sessionId: `discovery-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };

    logger.info('Starting schema discovery', discoveryOptions);
    emit('start-schema-discovery', discoveryOptions);

    setState(prev => ({
      ...prev,
      currentSession: discoveryOptions.sessionId,
      stage: 'connecting',
      progress: 0,
      message: 'Initiating schema discovery...',
      discoveredSchema: null,
      cubeSchema: null,
      errors: []
    }));
  }, [socket, isConnected, emit]);

  const generateCubeSchema = useCallback((schemaId: string, options?: {
    optimizationLevel?: 'performance' | 'accuracy' | 'balanced';
    includePreAggregations?: boolean;
  }) => {
    if (!socket || !isConnected) {
      logger.error('Cannot generate cube schema: WebSocket not connected');
      return;
    }

    const generationOptions = {
      schemaId,
      sessionId: state.currentSession,
      optimizationLevel: options?.optimizationLevel || 'balanced',
      includePreAggregations: options?.includePreAggregations !== false
    };

    logger.info('Generating cube schema', generationOptions);
    emit('generate-cube-schema', generationOptions);
  }, [socket, isConnected, emit, state.currentSession]);

  const cancelDiscovery = useCallback(() => {
    if (!socket || !isConnected || !state.currentSession) {
      return;
    }

    logger.info('Cancelling schema discovery', { sessionId: state.currentSession });
    emit('cancel-schema-discovery', { sessionId: state.currentSession });

    setState(prev => ({
      ...prev,
      stage: 'idle',
      currentSession: null,
      progress: 0,
      message: 'Discovery cancelled'
    }));

    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
  }, [socket, isConnected, emit, state.currentSession]);

  const retryDiscovery = useCallback(() => {
    if (state.stage === 'error' && state.currentSession) {
      logger.info('Retrying schema discovery', { sessionId: state.currentSession });
      emit('retry-schema-discovery', { sessionId: state.currentSession });

      setState(prev => ({
        ...prev,
        stage: 'connecting',
        progress: 0,
        message: 'Retrying discovery...',
        errors: []
      }));
    }
  }, [emit, state.stage, state.currentSession]);

  const resetState = useCallback(() => {
    setState({
      isConnected,
      currentSession: null,
      progress: 0,
      stage: 'idle',
      message: '',
      discoveredSchema: null,
      cubeSchema: null,
      errors: [],
      confidence: 0
    });

    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
  }, [isConnected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    ...state,
    isDiscovering: state.stage !== 'idle' && state.stage !== 'complete' && state.stage !== 'error',
    hasErrors: state.errors.length > 0,

    // Actions
    connect,
    disconnect,
    startSchemaDiscovery,
    generateCubeSchema,
    cancelDiscovery,
    retryDiscovery,
    resetState,

    // Event handlers
    on,
    off,

    // Utils
    canRetry: state.stage === 'error',
    isComplete: state.stage === 'complete',
    progressPercentage: Math.round(state.progress)
  };
};